// ----------------------------------------------------------------------------
// Snippet: Video Player
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Vimeo from '@vimeo/player';

// Styles
import './_video-block__controls.scss';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
var VIDEO_BLOCK = '.video-block';
var VIDEO = '.video-block__video';
var YOUTUBE_VIDEO = '.video-block--youtube';
var VIMEO_VIDEO = '.video-block--vimeo';
var HTML5_VIDEO = '.video-block--html5';
var CONTROLS_PLAY = '.video-block__control--play';
var CONTROLS_PAUSE = '.video-block__control--pause';

// Classes
var POSTERED_CLASS = 'video-block--is-postered';
var PAUSED_CLASS = 'video-block--is-paused';
var DYNAMIC_VIDEO_CLASS = 'video-block--dynamic-video';

// Events
var playerPlay = new Event('onplay.video-block');
var playerPause = new Event('onpause.video-block');
var play = new Event('play.video-block');
var pause = new Event('pause.video-block');

// -------------------------------------
// Helpers
// -------------------------------------

var videoBlocks = {
  init: function init() {
    var videoBlockElems = document.querySelectorAll(VIDEO_BLOCK);
    videoBlockElems.forEach(function (videoBlock) {
      videoBlocks.initVideoBlock(videoBlock);
    });
  },
  initVideoBlock: function initVideoBlock(videoBlock) {
    videoBlock.playButton = videoBlock.querySelector(CONTROLS_PLAY);
    videoBlock.pauseButton = videoBlock.querySelector(CONTROLS_PAUSE);
    videoBlock.querySelector(VIDEO).setAttribute('inert', '');
    videoBlock.addEventListener('onplay.video-block', function () {
      videoBlockControls.play(videoBlock);
    });
    videoBlock.addEventListener('onpause.video-block', function () {
      videoBlockControls.pause(videoBlock);
    });
    if (videoBlock.playButton !== null) {
      videoBlock.playButton.addEventListener('click', function () {
        var _videoBlock$pauseButt;
        videoBlockControls.play(videoBlock);
        videoBlock.dispatchEvent(play);
        (_videoBlock$pauseButt = videoBlock.pauseButton) === null || _videoBlock$pauseButt === void 0 || _videoBlock$pauseButt.focus();
        videoBlock.querySelector(VIDEO).removeAttribute('inert');
      });
    }
    if (videoBlock.pauseButton !== null) {
      videoBlock.pauseButton.addEventListener('click', function () {
        var _videoBlock$playButto;
        videoBlockControls.pause(videoBlock);
        videoBlock.dispatchEvent(pause);
        (_videoBlock$playButto = videoBlock.playButton) === null || _videoBlock$playButto === void 0 || _videoBlock$playButto.focus();
        videoBlock.querySelector(VIDEO).setAttribute('inert', '');
      });
    }
  }
};
var videoBlockControls = {
  play: function play(videoBlock) {
    videoBlock.classList.remove(POSTERED_CLASS);
    videoBlock.classList.remove(PAUSED_CLASS);
  },
  pause: function pause(videoBlock) {
    videoBlock.classList.add(POSTERED_CLASS);
    videoBlock.classList.add(PAUSED_CLASS);
  }
};
var youtubeVideos = {
  init: function init() {
    var tag = document.createElement('script');
    var firstScriptTag = document.getElementsByTagName('script')[0];
    tag.src = 'https://www.youtube.com/iframe_api';
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    window.onYouTubeIframeAPIReady = function () {
      youtubeVideos.initVideos();
    };
  },
  initVideos: function initVideos() {
    var videoBlocks = document.querySelectorAll(YOUTUBE_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = youtubeVideos.getVideo(videoBlock);
      var videoIframe = document.createElement('div');
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      video.appendChild(videoIframe);
      new window.YT.Player(videoIframe, {
        videoId: videoID,
        playerVars: {
          playlist: videoSettings.playlist_id == null && videoSettings.is_looped ? videoID : null,
          loop: videoSettings.is_looped ? 1 : 0,
          autoplay: videoSettings.is_autoplay ? 1 : 0,
          controls: videoSettings.is_controls ? 1 : 0,
          disablekb: videoSettings.is_controls ? 0 : 1,
          playsinline: videoSettings.is_autoplay ? 1 : 0,
          showinfo: 0,
          modestbranding: 1,
          fs: 1,
          cc_load_policy: 0,
          iv_load_policy: 3,
          rel: 0
        },
        events: {
          'onReady': youtubeVideos.onPlayerReady
        }
      });
    });
  },
  onPlayerReady: function onPlayerReady(event) {
    var player = event.target;
    var video = youtubeVideos.getVideo(player.getIframe().closest(VIDEO_BLOCK));
    var videoBlock = video.closest(VIDEO_BLOCK);
    var videoSettings = video.dataset.videoSettings;
    player.setVolume(videoSettings.is_muted ? 0 : 100);
    videoBlock.addEventListener('play.video-block', function () {
      youtubeVideos.play(player);
    });
    videoBlock.addEventListener('pause.video-block', function () {
      youtubeVideos.pause(player);
    });
    player.getIframe.tabIndex = -1;
  },
  play: function play(player) {
    var iframe = player.getIframe();
    iframe.tabIndex = 0;
    player.playVideo();
  },
  pause: function pause(player) {
    player.pauseVideo();
  },
  getVideo: function getVideo(videoBlock) {
    return videoBlock.querySelector(VIDEO);
  }
};
var vimeoVideos = {
  init: function init() {
    var videoBlocks = document.querySelectorAll(VIMEO_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = videoBlock.querySelector(VIDEO);
      var videoSettings = JSON.parse(video.dataset.videoSettings);
      var videoID = videoSettings.video_id;
      if (videoSettings.lookup_poster) {
        var url = 'https://vimeo.com/api/v2/video/' + videoID + '.json';
        fetch(url).then(response => response.json()).then(data => {
          videoBlock.classList.add(POSTERED_CLASS);
          videoBlock.style.backgroundImage = 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(' + data[0].thumbnail_large + ')';
        }).catch(() => {
          // error can be passed in as a parameter to be used
          videoBlock.classList.remove(POSTERED_CLASS);
          videoBlock.style.backgroundImage = '';
        });
      }
      var player = new Vimeo(video, {
        id: videoID,
        loop: videoSettings.is_looped,
        autoplay: videoSettings.is_autoplay,
        autopause: !videoSettings.is_autoplay,
        title: false,
        portrait: false,
        byline: false,
        controls: videoSettings.is_controls
      });
      player.setVolume(videoSettings.is_muted ? 0 : 1);
      videoBlock.addEventListener('play.video-block', function () {
        vimeoVideos.play(player);
      });
      videoBlock.addEventListener('pause.video-block', function () {
        vimeoVideos.pause(player);
      });
    });
  },
  play: function play(player) {
    player.play();
  },
  pause: function pause(player) {
    player.pause();
  }
};
var html5Videos = {
  init: function init() {
    var videoBlocks = document.querySelectorAll(HTML5_VIDEO);
    videoBlocks.forEach(function (videoBlock) {
      var video = videoBlock.querySelector(VIDEO);

      // If this video block has dynamic videos on mobile and desktop, get the appropriate video sources on mobile/desktop
      if (videoBlock.classList.contains(DYNAMIC_VIDEO_CLASS)) {
        var videoSources = JSON.parse(video.dataset.videoSources);
        var desktopVideoUrls = videoSources.desktop_video_source_urls.split(',');
        var desktopVideoSourceTypes = videoSources.desktop_video_source_types.split(',');
        var mobileVideoUrls = videoSources.mobile_video_source_urls.split(',');
        var mobileVideoSourceTypes = videoSources.mobile_video_source_types.split(',');
        var sourceUrls;
        var sourceTypes;
        if (window.innerWidth > 680 || mobileVideoUrls[0] == '') {
          // Load desktop videos. Fallback to Desktop videos if there is no mobile video uploaded
          sourceUrls = desktopVideoUrls;
          sourceTypes = desktopVideoSourceTypes;
        } else {
          // Load mobile videos
          sourceUrls = mobileVideoUrls;
          sourceTypes = mobileVideoSourceTypes;
        }

        // Loop through all the source urls and types then insert the sources to the video element
        for (var index = 0; index < sourceUrls.length; index++) {
          video.insertAdjacentHTML('beforeend', "<source type='".concat(sourceTypes[index], "' src='").concat(sourceUrls[index], "'/>"));
        }
      }
      videoBlock.addEventListener('play.video-block', function () {
        video.play();
      });
      videoBlock.addEventListener('pause.video-block', function () {
        video.pause();
      });
      video.onplay = function () {
        videoBlock.dispatchEvent(playerPlay);
      };
      video.onpause = function () {
        videoBlock.dispatchEvent(playerPause);
      };
    });
  }
};

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  videoBlocks.init();
  youtubeVideos.init();
  vimeoVideos.init();
  html5Videos.init();
});